import { BaseURL } from "@/config/apiUrl";
import store from "@/store";
import { signOutRequest } from "@/store/auth/authSlice";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";

const getHeaders = (accessToken) => ({
  Accept: "application/json",
  Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
  "ngrok-skip-browser-warning": "69420",
  "timezone-offset": moment().utcOffset(),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const handleError = (error, showAlert, isDispatch) => {
  let errorMessage = "";
  if (Array.isArray(error?.response?.data?.message?.error)) {
    error?.response?.data?.message?.error?.forEach(
      (item) => (errorMessage += ` • ${item} \n`),
    );
  } else {
    errorMessage = error?.response?.data?.message?.error;
  }

  if (error.response?.status === 401) {
    const dispatch = store.dispatch;
    toast.error(
      "Your session has either expired or your account is inactive. Please log in again to continue.",
      {
        position: "top-center",
      },
    );
    dispatch(signOutRequest());
  }

  if (showAlert) {
    const message =
      error.message === "Network Error"
        ? `${error.message} : Please Check Your Network Connection`
        : errorMessage;
    toast.error(message, { position: "top-center" });
  }
};

export const Get = async (
  route,
  accessToken,
  showAlert = true,
  dispatch,
  { timeout } = {},
) => {
  const options = {
    headers: getHeaders(accessToken),
    ...(timeout ? { timeout } : {}),
  };
  try {
    const response = await axios.get(route, options);
    return response;
  } catch (error) {
    handleError(error, showAlert, dispatch);
  }
};

const apiRequest = async (
  method,
  route,
  data,
  { headers, timeout },
  showAlert,
  dispatch,
) => {
  const options = {
    method,
    url: route,
    ...(data && { data }),
    headers,
    // Left unset by default, which is axios' "wait forever". A caller opts in
    // where waiting forever is worse than failing — see TEST_REQUEST_TIMEOUT.
    ...(timeout ? { timeout } : {}),
  };
  try {
    return await axios(options);
  } catch (error) {
    handleError(error, showAlert, dispatch);
  }
};

export const Post = (route, data, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("post", route, data, apiHeaders, showAlert, dispatch);
};

export const Patch = (route, data, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("patch", route, data, apiHeaders, showAlert, dispatch);
};

export const Put = (route, data, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("put", route, data, apiHeaders, showAlert, dispatch);
};

export const Delete = (route, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("delete", route, null, apiHeaders, showAlert, dispatch);
};

const UploadFilesToAwsBySignedUrl = async (urlsArray, files) => {
  const promises = urlsArray.map((url, index) => {
    const contentType = files?.[index]?.type || "application/octet-stream";
    return axios.put(url, files[index], {
      headers: {
        "Content-Type": contentType,
        "ngrok-skip-browser-warning": "69420",
      },
    });
  });
  const response = await Promise.allSettled(promises);
  return response;
};
export const uploadMedia = async (selectedFiles) => {
  if (selectedFiles?.length === 0) return [];
  const params = {
    files: selectedFiles?.map((ele) => ({
      mimeType: ele?.type || "application/octet-stream",
      name: ele?.name,
    })),
  };
  const apiUrl = BaseURL("get-signed-url");
  const response = await Post(apiUrl, params);
  if (response) {
    if (response?.data?.urls?.length > 0) {
      await UploadFilesToAwsBySignedUrl(response?.data?.urls, selectedFiles);
    }
    return response?.data?.keys;
  }
  return null;
};
